import {produce, Draft} from 'immer';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
import {unstable_batchedUpdates} from 'react-dom';
import {applyNodeChanges, Edge, Node, ReactFlowInstance, Connection, useStoreApi} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {removeFunctionSpecsUsingEdge} from 'processes/graph-builder-processes/flowchart-viz/action-functions/deleteEdgeProcessFlowchartActionFunction';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {getRouterNodeIdsBetweenNodes} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
  UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC,
  UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
} from 'processes/modals/router-config/RouterConfigConstants';
import {isNewEdgeValidForFlowchart} from 'processes/modals/utils/TransitionModalValidations';
import {ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import runModal from 'ui-library/runModal';
import * as keyboard from 'util/Keyboard';
import {omit} from 'util/omitKey';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {AddNodeOption, getAddNodeOptions} from '../AddNodeDropdown';
import buildInteractiveFlowchartFromStaticData from '../buildInteractiveFlowchartFromStaticData';
import {buildControlFlowEdge} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {
  processEdgeChangesWithMirroring,
  mirrorTransitionSource,
  mirrorCustomMappingData,
} from '../edgeMirroringUtils';
import {getEdgeTypeFromHandleId} from '../flowchartIdentifierUtils';
import FlowchartRemoveNodeModal from '../modals/FlowchartRemoveNodeModal';
import {
  FlowchartEdge,
  FlowchartStaticEdgeData,
  INTERACTION_FIELDS,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  StaticTemplateFlowchart,
} from '../processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isStaticRouterNodeData,
  RouterStaticNodeData,
  StaticFlowchartNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {
  FlowchartCustomMappingDataChange,
  FlowchartCustomMappingDataChangeType,
  FlowchartEdgeChange,
  FlowchartEdgeChangeType,
  FlowchartMetadataOptionsChange,
  FlowchartMetadataOptionsChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartOptions,
  FlowchartTransitionSourceChange,
  FlowchartTransitionSourceChangeType,
} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigWithPartialMetadata} from '../redux/FlowchartConfigStateManager';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {SingletonTaskSchemasByName} from '../SingletonTaskSchemasContext';
import {getNodeIdPairFromTransitionSourceKey} from '../TransitionSourceUtils';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {centerNodeInVisibleCanvas} from './centerNodeInVisibleCanvas';

/**
 * Callers will generate the changes to be applied to a given draft flowchart state,
 * which applyFlowchartChanges will then apply to the draft state.
 */
export type FlowchartChangeGenerator = (
  flowchartConfig: FlowchartConfigWithMetadata
) => Partial<FlowchartChanges>;

export type ApplyFlowchartChangesFn = (
  changeGenerator: FlowchartChangeGenerator,
  saveImmediately?: boolean,
  skipSettingDirtyState?: boolean
) => Promise<FlowchartConfigMetadata | null>;

interface UseFlowchartStateReturn {
  // Interactive state
  interactiveNodes: Array<FlowchartNode>;
  interactiveEdges: Array<FlowchartEdge>;
  setInteractiveNodes: React.Dispatch<React.SetStateAction<Array<FlowchartNode>>>;
  // Core functions
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  onReactFlowNodeChangesOnly: (nodeChanges: Array<FlowchartNodeChange>) => void;
  onReactFlowEdgeChangesOnly: (edgeChanges: Array<FlowchartEdgeChange>) => void;
  onConnect: (connection: Connection) => void;
  setNodesAndEdgesViaChangesForLayout: (newNodes: Array<Node>, newEdges: Array<Edge>) => void;
  // Sidepanel state
  isLeftSidepanelOpen: boolean;
  setIsLeftSidepanelOpen: (isOpen: boolean) => void;
  isRightSidepanelOpen: boolean;
  selectedFieldId: string | null;
  selectedNodeIdForRightSidepanel: string | null;
  setSelectedFieldId: (fieldId: string | null) => void;
  setSelectedNodeIdForRightSidepanel: (nodeId: string | null) => void;
  // Sidepanel functions
  handleRightSidepanelClose: () => void;
  revertNodeSelection: (nodeId: string, fieldId?: string | null) => void;
  // Node addition state
  interactiveFlowchartOptions: FlowchartOptions;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
  // Node deletion state
  nodesBeingDeletedRef: React.MutableRefObject<Set<string> | null>;
  // Node dimensions
  getMeasuredDimensionsFromStore: (nodeId: string) => {width: number; height: number};
}

export interface FlowchartChangeResult {
  updatedNodes: Array<FlowchartNode>;
  updatedEdges: Array<FlowchartEdge>;

  /**
   * Whether or not the static data of any nodes has changed.
   * This includes whether or not any nodes were added or removed.
   */
  hasNodeDataChanges: boolean;

  /**
   * Whether or not the structure of the flowchart has changed.
   * This includes whether or not any edges were added or removed.
   */
  hasStructuralChanges: boolean;
}

export interface UseFlowchartStateParams {
  staticFlowchart: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  validationErrors: Array<ProcessFlowchartValidationError>;
  setFlowchartAndMetadata: (
    updater: (currentState: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
    saveImmediately?: boolean,
    skipSettingDirtyState?: boolean
  ) => Promise<FlowchartConfigMetadata | null>;
  // Extra parameters needed for adding a node via a right handle click
  addNodeParams: {
    flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
    taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
    didInitialFlowchartHaveChainedRouters: boolean;
    singletonTaskSchemasByName: SingletonTaskSchemasByName;
  };
  // Parameters for centering nodes when sidepanel opens
  reactFlowInstance?: ReactFlowInstance;
  reactFlowWrapper?: React.RefObject<HTMLDivElement>;
  leftSidepanelRef?: React.RefObject<HTMLDivElement>;
  rightSidepanelRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Validates edge changes and filters out invalid changes.
 */
function validateEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  existingEdges: Array<FlowchartStaticEdgeData>
): Array<FlowchartEdgeChange> {
  return edgeChanges.filter((edgeChange) => {
    // Only validate 'add' changes, others are always valid
    if (edgeChange.type !== FlowchartEdgeChangeType.ADD || !edgeChange.item) {
      return true;
    }

    return isNewEdgeValidForFlowchart(edgeChange.item, existingEdges);
  });
}

function getOrphanedEdgeIdsAfterNodeRemoval(
  draft: Draft<FlowchartConfigWithMetadata>,
  removedNodeId: string
): Array<string> {
  return draft.staticFlowchart.edgesStaticData
    .filter((edge) => edge.source === removedNodeId || edge.target === removedNodeId)
    .map((edge) => edge.id);
}

function getEdgesRouterFunctionsDifference(
  routerConfig: RouterConfig,
  numRouterNodeOutboundEdges: number
): number {
  const numExistingFunctionSpecs =
    routerConfig.functionSpecs.length + (routerConfig.defaultFunctionSpec != null ? 1 : 0);
  const numEdgesWithoutFunctionSpecs = numRouterNodeOutboundEdges - numExistingFunctionSpecs;

  return numEdgesWithoutFunctionSpecs;
}

function addUnconfiguredFunctionsForRouterConfig(
  routerConfig: RouterConfig,
  numEdgesWithoutFunctionSpecs: number,
  numRouterOutboundEdges: number
): RouterConfig {
  const newFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec> = [];
  const numNewFunctionSpecsNeeded =
    routerConfig.defaultFunctionSpec == null
      ? numEdgesWithoutFunctionSpecs - 1
      : numEdgesWithoutFunctionSpecs;

  let newFunctionSpecCreated = 0;
  let defaultFunctionSpec = routerConfig.defaultFunctionSpec;
  if (defaultFunctionSpec == null) {
    defaultFunctionSpec = {...UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC, clientId: uuidv4()};
    newFunctionSpecCreated++;
  }
  while (newFunctionSpecCreated < numNewFunctionSpecsNeeded) {
    newFunctionSpecs.push({
      ...UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
      name: `Route ${numRouterOutboundEdges - numNewFunctionSpecsNeeded + newFunctionSpecCreated}`,
      clientId: uuidv4(),
    });
    newFunctionSpecCreated++;
  }
  return {
    ...routerConfig,
    functionSpecs: routerConfig.functionSpecs.concat(newFunctionSpecs),
    defaultFunctionSpec,
  };
}

function removeUnusedFunctionSpecs(
  routerConfig: RouterConfig,
  numEdgesWithoutFunctionSpecs: number
): RouterConfig {
  assert(numEdgesWithoutFunctionSpecs > 0, 'Expected numEdgesWithoutFunctionSpecs to be positive');
  const newFunctionSpecs = [];
  let functionSpecsRemoved = 0;
  for (const functionSpec of routerConfig.functionSpecs) {
    if (
      functionSpec.edgeConfigReference != null ||
      functionSpecsRemoved >= Math.abs(numEdgesWithoutFunctionSpecs)
    ) {
      newFunctionSpecs.push(functionSpec);
    } else {
      functionSpecsRemoved++;
    }
  }

  const numSpecsStillToRemove = numEdgesWithoutFunctionSpecs - functionSpecsRemoved;
  let defaultFunctionSpec = routerConfig.defaultFunctionSpec;
  if (numSpecsStillToRemove > 0 && defaultFunctionSpec?.edgeConfigReference == null) {
    defaultFunctionSpec = null;
    functionSpecsRemoved++;
  }
  assert(
    functionSpecsRemoved === numEdgesWithoutFunctionSpecs,
    'Did not remove expected number of function specs',
    {
      functionSpecsRemoved,
      numEdgesWithoutFunctionSpecs,
      defaultFunctionSpec,
    }
  );

  routerConfig = {
    ...routerConfig,
    functionSpecs: newFunctionSpecs,
    defaultFunctionSpec,
  };
  return routerConfig;
}

/**
 * Adding/removing an edge requires recalculating function specs for router nodes.
 */
function updateRouterConfigsAfterEdgeChange(
  draft: Draft<FlowchartConfigWithMetadata>,
  changeType: FlowchartEdgeChangeType.ADD | FlowchartEdgeChangeType.REMOVE,
  edgeId?: string
): void {
  // Only process if we have router nodes
  const routerNodes = draft.staticFlowchart.nodesStaticData.filter(
    (node) => node.nodeType === ProcessFlowchartNodeType.ROUTER_NODE
  ) as Array<RouterStaticNodeData>;

  if (routerNodes.length === 0) {
    return;
  }

  for (let i = 0; i < draft.staticFlowchart.nodesStaticData.length; i++) {
    const node = draft.staticFlowchart.nodesStaticData[i];

    if (node.nodeType !== ProcessFlowchartNodeType.ROUTER_NODE) {
      continue;
    }

    const routerNode = node as RouterStaticNodeData;
    let updatedRouterConfig: RouterConfig = {...routerNode.routerConfig};

    // Handle edge removal
    if (changeType === FlowchartEdgeChangeType.REMOVE && edgeId) {
      updatedRouterConfig = removeFunctionSpecsUsingEdge(edgeId, updatedRouterConfig);
    }

    // Recalculate function specs based on current edges
    const numOutboundEdges = draft.staticFlowchart.edgesStaticData.filter(
      (edge) => edge.source === node.id
    ).length;
    const numEdgesWithoutFunctionSpecs = getEdgesRouterFunctionsDifference(
      updatedRouterConfig,
      numOutboundEdges
    );

    if (numEdgesWithoutFunctionSpecs > 0) {
      updatedRouterConfig = addUnconfiguredFunctionsForRouterConfig(
        updatedRouterConfig,
        numEdgesWithoutFunctionSpecs,
        numOutboundEdges
      );
    } else if (numEdgesWithoutFunctionSpecs < 0) {
      updatedRouterConfig = removeUnusedFunctionSpecs(
        updatedRouterConfig,
        Math.abs(numEdgesWithoutFunctionSpecs)
      );
    }

    // Update the node in the draft
    (draft.staticFlowchart.nodesStaticData[i] as RouterStaticNodeData).routerConfig = updatedRouterConfig;
  }
}

function getEmptyFlowchartChanges(): FlowchartChanges {
  return {
    nodeChanges: [],
    edgeChanges: [],
    transitionSourceChanges: [],
    customMappingChanges: [],
    metadataOptionsChanges: [],
  };
}

function applyFlowchartNodeChanges(
  nodeChanges: Array<FlowchartNodeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  nodeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartNodeChangeType.ADD:
        if (change.item != null) {
          const newNodeData: StaticFlowchartNodeData = extractStaticData(change.item.data);
          draft.staticFlowchart.nodesStaticData.push(newNodeData);
          // Set initial position in local state
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.item.id]: change.item.position || {x: 0, y: 0},
          }));
        }
        break;
      case FlowchartNodeChangeType.POSITION:
        if (change.position) {
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.id]: change.position as {x: number; y: number},
          }));
        }
        break;
      case FlowchartNodeChangeType.SELECT: {
        // Only modify the Set if the change would actually change it (prevents unnecessary re-renders)
        const isCurrentlySelected = draft.metadata.interactionState.selectedNodeIds.has(change.id);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.add(change.id);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        }
        break;
      }
      case FlowchartNodeChangeType.REMOVE:
        assert(
          draft.staticFlowchart.nodesStaticData.some((node) => node.id === change.id),
          'Node does not exist in static data'
        );
        draft.staticFlowchart.nodesStaticData = draft.staticFlowchart.nodesStaticData.filter(
          (node) => node.id !== change.id
        );
        draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        // Clean up local position state
        setNodePositions((prevPositions) => {
          const newPositions = {...prevPositions};
          delete newPositions[change.id];
          return newPositions;
        });

        // Post-processing
        sideEffects.edgeChanges.push(
          ...getOrphanedEdgeIdsAfterNodeRemoval(draft, change.id).map((edgeId) => ({
            type: FlowchartEdgeChangeType.REMOVE as const,
            id: edgeId,
          }))
        );
        break;
      case FlowchartNodeChangeType.REPLACE: {
        const nodeToReplace = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
        if (nodeToReplace) {
          const newNodeData: StaticFlowchartNodeData = extractStaticData(change.data);
          Object.assign(nodeToReplace, newNodeData);
        }
        break;
      }
      case FlowchartNodeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected node change type', {change});
    }
  });

  return sideEffects;
}

function applyFlowchartEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  // Pre-processing
  const allEdgeChanges = processEdgeChangesWithMirroring(edgeChanges, draft, {
    customMappingDataByEdgeId: draft.metadata.customMappingDataByEdgeId,
    onChangeCustomMappingData: (edgeId, newCustomMappingData) => {
      draft.metadata.customMappingDataByEdgeId[edgeId] = newCustomMappingData;
    },
    transitionSourceByNodeIdPair: draft.metadata.transitionSourceByNodeIdPair,
    onChangeTransitionSource: (key, newTransitionSource) => {
      draft.metadata.transitionSourceByNodeIdPair[key] = newTransitionSource;
    },
  });
  const validatedEdgeChanges = validateEdgeChanges(allEdgeChanges, draft.staticFlowchart.edgesStaticData);

  validatedEdgeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartEdgeChangeType.ADD:
        if (change.item != null) {
          assert(change.item.sourceHandle != null, 'Source handle is required');
          assert(change.item.targetHandle != null, 'Target handle is required');
          assert(change.item.type != null, 'Type is required');
          const newEdge: FlowchartStaticEdgeData = {
            id: change.item.id,
            source: change.item.source,
            target: change.item.target,
            sourceHandle: change.item.sourceHandle,
            targetHandle: change.item.targetHandle,
            type: change.item.type as ProcessFlowchartEdgeType,
          };
          draft.staticFlowchart.edgesStaticData.push(newEdge);

          // Post-processing
          updateRouterConfigsAfterEdgeChange(draft, FlowchartEdgeChangeType.ADD);
        }
        break;
      case FlowchartEdgeChangeType.SELECT:
        if (change.selected) {
          draft.metadata.interactionState.selectedEdgeIds.add(change.id);
        } else {
          draft.metadata.interactionState.selectedEdgeIds.delete(change.id);
        }
        break;
      case FlowchartEdgeChangeType.REMOVE:
        draft.staticFlowchart.edgesStaticData = draft.staticFlowchart.edgesStaticData.filter(
          (edge) => edge.id !== change.id
        );
        draft.metadata.interactionState.selectedEdgeIds.delete(change.id);

        sideEffects.customMappingChanges.push({
          type: FlowchartCustomMappingDataChangeType.REMOVE,
          id: change.id,
        });

        // Post-processing
        updateRouterConfigsAfterEdgeChange(draft, FlowchartEdgeChangeType.REMOVE, change.id);
        break;
      case FlowchartEdgeChangeType.LABEL: {
        draft.metadata.interactionState.edgeLabelsByEdgeId[change.id] = change.label;
        const targetEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === change.id);
        assert(targetEdge != null, 'Target edge should not be null');
        targetEdge.label = change.label;
        break;
      }
      case FlowchartEdgeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected edge change type', {change});
    }
  });

  return sideEffects;
}

function applyTransitionSourceChanges(
  transitionSourceChanges: Array<FlowchartTransitionSourceChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  transitionSourceChanges.forEach((change) => {
    const originalTransitionSource = draft.metadata.transitionSourceByNodeIdPair[change.id];
    draft.metadata.transitionSourceByNodeIdPair[change.id] = change.transitionSource;

    if (change.transitionSource !== originalTransitionSource) {
      const {sourceNodeId, targetNodeId} = getNodeIdPairFromTransitionSourceKey(change.id);
      const routerNodeIds = getRouterNodeIdsBetweenNodes(
        {
          nodes: draft.staticFlowchart.nodesStaticData.map((node) => ({id: node.id, data: node})),
          edges: draft.staticFlowchart.edgesStaticData,
        },
        sourceNodeId,
        targetNodeId
      );
      for (const routerNodeId of routerNodeIds) {
        const routerNode = draft.staticFlowchart.nodesStaticData.find((node) => node.id === routerNodeId)!;
        assert(isStaticRouterNodeData(routerNode), 'Expected router node');
        // When the transition source is changed, we need to clear the expressions on all router nodes between
        // the two nodes because the router configurations depend on the transition source.
        // We do not need to clear the default function spec because it does not have an expression associated
        // with it.
        const newData = produce(routerNode, (draftRouterNode) => {
          draftRouterNode.routerConfig.functionSpecs = draftRouterNode.routerConfig.functionSpecs.map(
            (spec) => ({
              ...spec,
              expression: null,
              isConfigured: false,
            })
          );
        });
        sideEffects.nodeChanges.push({
          id: routerNodeId,
          type: FlowchartNodeChangeType.REPLACE,
          data: newData,
        });
      }
      mirrorTransitionSource(
        change.id,
        change.transitionSource,
        draft.staticFlowchart.edgesStaticData,
        draft.staticFlowchart.nodesStaticData,
        (key, transitionSource) => {
          sideEffects.transitionSourceChanges.push({
            type: FlowchartTransitionSourceChangeType.REPLACE,
            id: key,
            transitionSource,
          });
        },
        (key) => {
          delete draft.metadata.transitionSourceByNodeIdPair[key];
        }
      );
    }
  });

  return sideEffects;
}

function applyCustomMappingChanges(
  customMappingChanges: Array<FlowchartCustomMappingDataChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  customMappingChanges.forEach((change) => {
    const customMappingValue =
      change.type === FlowchartCustomMappingDataChangeType.ADD ||
      change.type === FlowchartCustomMappingDataChangeType.REPLACE
        ? change.customMapping
        : null;

    switch (change.type) {
      case FlowchartCustomMappingDataChangeType.ADD:
        draft.metadata.customMappingDataByEdgeId[change.id] = change.customMapping;
        break;
      case FlowchartCustomMappingDataChangeType.REMOVE:
        delete draft.metadata.customMappingDataByEdgeId[change.id];
        break;
      case FlowchartCustomMappingDataChangeType.REPLACE:
        draft.metadata.customMappingDataByEdgeId[change.id] = change.customMapping;
        break;
      default:
        assert(false, 'Unexpected custom mapping data change type', {change});
        break;
    }

    mirrorCustomMappingData(
      change.id,
      customMappingValue,
      draft.staticFlowchart.edgesStaticData,
      draft.staticFlowchart.nodesStaticData,
      (edgeId, customMapping) => {
        draft.metadata.customMappingDataByEdgeId[edgeId] = customMapping;
      },
      (edgeId) => {
        delete draft.metadata.customMappingDataByEdgeId[edgeId];
      }
    );
  });

  return sideEffects;
}

function applyMetadataOptionsChanges(
  metadataOptionsChanges: Array<FlowchartMetadataOptionsChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  metadataOptionsChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartMetadataOptionsChangeType.UPDATE:
        draft.metadata.options = {...draft.metadata.options, ...change.options};
        break;
      default:
        assert(false, 'Unexpected options change type', {change});
    }
  });

  return sideEffects;
}

/**
 * Detects which parts of the flowchart state have changed and returns only the changed parts.
 *
 * Uses Immer's structural sharing: unchanged parts keep their original references, while changed parts get new references.
 * This enables fast reference equality checks to identify what actually changed.
 *
 * We return only changed parts (not the entire state) so reduceFlowchartConfig can merge these changes with the latest server state.
 */
function detectChangedParts(
  original: FlowchartConfigWithMetadata,
  updated: FlowchartConfigWithMetadata
): FlowchartConfigWithPartialMetadata | null {
  // Early short-circuit: if both references are the same, nothing changed
  // (This only happens if produce() was never called)
  if (original === updated) {
    return null;
  }

  const staticFlowchartChanged = updated.staticFlowchart !== original.staticFlowchart;

  const interactionStateChanged = updated.metadata.interactionState !== original.metadata.interactionState;
  const transitionSourceChanged =
    updated.metadata.transitionSourceByNodeIdPair !== original.metadata.transitionSourceByNodeIdPair;
  const customMappingChanged =
    updated.metadata.customMappingDataByEdgeId !== original.metadata.customMappingDataByEdgeId;
  const metadataOptionsChanged = updated.metadata.options !== original.metadata.options;

  const anyMetadataChanged =
    interactionStateChanged || transitionSourceChanged || customMappingChanged || metadataOptionsChanged;

  // Early short-circuit: if nothing actually changed, return null
  if (!staticFlowchartChanged && !anyMetadataChanged) {
    return null;
  }

  // Build the result object based on what actually changed
  const buildMetadataChanges = (): Partial<FlowchartConfigMetadata> => {
    const metadataChanges: Partial<FlowchartConfigMetadata> = {};

    if (interactionStateChanged) {
      metadataChanges.interactionState = updated.metadata.interactionState;
    }

    if (transitionSourceChanged) {
      metadataChanges.transitionSourceByNodeIdPair = updated.metadata.transitionSourceByNodeIdPair;
    }

    if (customMappingChanged) {
      metadataChanges.customMappingDataByEdgeId = updated.metadata.customMappingDataByEdgeId;
    }

    if (metadataOptionsChanged) {
      metadataChanges.options = updated.metadata.options;
    }

    return metadataChanges;
  };

  if (staticFlowchartChanged && anyMetadataChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  } else if (staticFlowchartChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
    };
  } else {
    return {
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  }
}

/**
 * Extracts the static data from the data object of a flowchart node.
 * @param nodeData The data object on a FlowchartNode.
 * @returns The static data of the node.
 */
export function extractStaticData(nodeData: FlowchartNode['data']): StaticFlowchartNodeData {
  return omit(
    nodeData,
    ...(INTERACTION_FIELDS as unknown as Array<keyof FlowchartNode['data']>)
  ) as StaticFlowchartNodeData;
}

export function extractStaticEdgeData(edge: FlowchartEdge): FlowchartStaticEdgeData {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    type: edge.type,
    label: edge.label,
  };
}

export interface FlowchartChanges {
  nodeChanges: Array<FlowchartNodeChange>;
  edgeChanges: Array<FlowchartEdgeChange>;
  transitionSourceChanges: Array<FlowchartTransitionSourceChange>;
  customMappingChanges: Array<FlowchartCustomMappingDataChange>;
  metadataOptionsChanges: Array<FlowchartMetadataOptionsChange>;
}

/**
 * Core logic for applying flowchart changes to static data, metadata, and interactive state.
 */
export function applyFlowchartChangesCore(
  currentState: FlowchartConfigWithMetadata,
  changes: Partial<FlowchartChanges>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>
): FlowchartConfigWithPartialMetadata | null {
  const {
    nodeChanges = [],
    edgeChanges = [],
    transitionSourceChanges = [],
    customMappingChanges = [],
    metadataOptionsChanges = [],
  } = changes;

  let currSideEffects: FlowchartChanges = {
    nodeChanges,
    edgeChanges,
    transitionSourceChanges,
    customMappingChanges,
    metadataOptionsChanges,
  };

  const getDoesSideEffectsHaveChanges = (sideEffects: FlowchartChanges): boolean =>
    Object.values(sideEffects).some((changeArray) => changeArray.length > 0);

  if (!getDoesSideEffectsHaveChanges(currSideEffects)) {
    return null;
  }

  const updatedFlowchartAndMetadata = produce(currentState, (draft) => {
    do {
      const nodeSideEffects = applyFlowchartNodeChanges(currSideEffects.nodeChanges, draft, setNodePositions);
      const edgeSideEffects = applyFlowchartEdgeChanges(currSideEffects.edgeChanges, draft);
      const transitionSourceSideEffects = applyTransitionSourceChanges(
        currSideEffects.transitionSourceChanges,
        draft
      );
      const customMappingSideEffects = applyCustomMappingChanges(currSideEffects.customMappingChanges, draft);
      const metadataOptionsSideEffects = applyMetadataOptionsChanges(
        currSideEffects.metadataOptionsChanges,
        draft
      );

      const allSideEffects = [
        nodeSideEffects,
        edgeSideEffects,
        transitionSourceSideEffects,
        customMappingSideEffects,
        metadataOptionsSideEffects,
      ];
      currSideEffects = allSideEffects.reduce(
        (acc, curr) => ({
          nodeChanges: [...acc.nodeChanges, ...curr.nodeChanges],
          edgeChanges: [...acc.edgeChanges, ...curr.edgeChanges],
          transitionSourceChanges: [...acc.transitionSourceChanges, ...curr.transitionSourceChanges],
          customMappingChanges: [...acc.customMappingChanges, ...curr.customMappingChanges],
          metadataOptionsChanges: [...acc.metadataOptionsChanges, ...curr.metadataOptionsChanges],
        }),
        getEmptyFlowchartChanges()
      );
    } while (getDoesSideEffectsHaveChanges(currSideEffects));
  });

  return detectChangedParts(currentState, updatedFlowchartAndMetadata);
}

/**
 * Contains the core flowchart state management logic for the horizontal flowchart.
 *
 * Main features:
 * - Apply changes to the flowchart and/or metadata
 * - Handle right sidepanel state management
 * - Handle reactflow actions related to nodes + edges
 *
 * Core concepts:
 * - Minimal flowchart representations to actively track:
 *      We should rarely, if ever, need to mutate or interact with the interactive nodes/edges directly,
 *      which gets sent to reactflow for rendering. Instead, we change the static flowchart, metadata and interactive
 *      state trackers as needed, and the interactive flowchart is rebuilt from those sources of truth automatically.
 * - All change management is funneled through the applyFlowchartChanges function:
 *      This allows for centralizing side effects and autohealing logic related to flowchart changes.
 *

 * @returns The flowchart state management logic.
 */
const useFlowchartState = (params: UseFlowchartStateParams): UseFlowchartStateReturn => {
  const {
    staticFlowchart,
    flowchartConfigMetadata,
    validationErrors,
    setFlowchartAndMetadata,
    addNodeParams,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
  } = params;

  const locked = ReactFlowLockedContext.useContext() ?? false;
  const reactFlowStore = useStoreApi();

  const getDefaultNodeDimensions = useCallback((): {width: number; height: number} => {
    const fallbackNodeHeight = COMMON_NODE_WIDTH * 2;
    return {
      width: COMMON_NODE_WIDTH,
      height: fallbackNodeHeight,
    };
  }, []);

  const getMeasuredDimensionsFromStore = useCallback(
    (nodeId: string): {width: number; height: number} => {
      const {nodeInternals} = reactFlowStore.getState();
      const nodeInternal = nodeInternals.get(nodeId);

      const width = nodeInternal?.width;
      const height = nodeInternal?.height;
      if (width != null && height != null && width > 0 && height > 0) {
        return {width, height};
      }

      return getDefaultNodeDimensions();
    },
    [reactFlowStore, getDefaultNodeDimensions]
  );

  const [interactiveNodes, setInteractiveNodes] = useState<Array<FlowchartNode>>([]);
  const [interactiveEdges, setInteractiveEdges] = useState<Array<FlowchartEdge>>([]);
  // By node ID
  const [nodePositions, setNodePositions] = useState<Record<string, {x: number; y: number}>>({});

  // Ref to track current node positions for use in rebuilds during drag operations
  // This allows us to preserve drag positions without triggering full flowchart rebuilds
  const currentNodePositionsRef = useRef<Record<string, {x: number; y: number}>>({});

  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(false);
  const [isRightSidepanelOpen, setIsRightSidepanelOpen] = useState(false);
  const [selectedNodeIdForRightSidepanel, setSelectedNodeIdForRightSidepanel] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  const [_sourceNodeAndNewNodeBeingAdded, setSourceNodeAndNewNodeBeingAdded] = useState<
    [string, FlowchartNode] | null
  >(null);

  const [placeholderNodeIdBeingAdded, setPlaceholderNodeBeingAdded] = useState<string | null>(null);

  const nodesBeingDeletedRef = useRef<Set<string> | null>(null);

  const applyFlowchartChanges = useCallback(
    (
      changeGenerator: FlowchartChangeGenerator,
      saveImmediately: boolean = false,
      skipSettingDirtyState: boolean = false
    ): Promise<FlowchartConfigMetadata | null> =>
      setFlowchartAndMetadata(
        (currentState) => {
          if (!currentState) {
            return null;
          }

          const changes = changeGenerator(currentState);

          // When locked, allow selection changes so the sidepanel can open in read-only mode,
          // but block all other changes (add/remove/replace nodes, edge changes, metadata changes)
          if (locked) {
            const onlySelection =
              (changes.nodeChanges ?? []).every((c) => c.type === FlowchartNodeChangeType.SELECT) &&
              (changes.edgeChanges ?? []).every((c) => c.type === FlowchartEdgeChangeType.SELECT) &&
              !changes.transitionSourceChanges?.length &&
              !changes.customMappingChanges?.length;
            if (!onlySelection) {
              return null;
            }
          }
          return applyFlowchartChangesCore(currentState, changes, setNodePositions);
        },
        saveImmediately,
        skipSettingDirtyState
      ),
    [setFlowchartAndMetadata, locked]
  );

  const openSidepanelAndSelectNode = useCallback(
    (nodeId: string, fieldId: string | null = null): void => {
      setIsRightSidepanelOpen(true);
      setSelectedNodeIdForRightSidepanel(nodeId);
      setSelectedFieldId(fieldId);

      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes
        const currentlySelectedNodeIds =
          flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds;
        currentlySelectedNodeIds.forEach((selectedId: string) => {
          if (selectedId !== nodeId) {
            nodeChanges.push({
              type: FlowchartNodeChangeType.SELECT,
              id: selectedId,
              selected: false,
            });
          }
        });

        // Select the target node
        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });

        return {nodeChanges};
      });

      // Center the node in the visible canvas after sidepanel opens
      if (reactFlowInstance && reactFlowWrapper?.current) {
        // Use setTimeout to ensure DOM has updated after sidepanel opens
        setTimeout(() => {
          const nodePosition = nodePositions[nodeId];
          const dimensions = getMeasuredDimensionsFromStore(nodeId);
          if (nodePosition) {
            centerNodeInVisibleCanvas({
              reactFlowInstance,
              reactFlowWrapper,
              leftSidepanelRef: leftSidepanelRef ?? {current: null},
              rightSidepanelRef: rightSidepanelRef ?? {current: null},
              nodePosition,
              nodeWidth: dimensions.width,
              nodeHeight: dimensions.height,
            });
          }
        }, 0);
      }
    },
    [
      applyFlowchartChanges,
      reactFlowInstance,
      reactFlowWrapper,
      leftSidepanelRef,
      rightSidepanelRef,
      nodePositions,
      getMeasuredDimensionsFromStore,
    ]
  );

  const openSidepanelForNode = useCallback(
    (nodeId: string): void => {
      openSidepanelAndSelectNode(nodeId, null);
    },
    [openSidepanelAndSelectNode]
  );

  const openSidepanelForNodeWithField = useCallback(
    (nodeId: string, fieldId: string): void => {
      openSidepanelAndSelectNode(nodeId, fieldId);
    },
    [openSidepanelAndSelectNode]
  );

  const getAddNodeOptionsForRightHandle = useCallback(
    (sourceNodeId: string, reactFlowInstance: ReactFlowInstance): Array<AddNodeOption> | null =>
      getAddNodeOptions({
        flowchartEditorConfigurator: addNodeParams.flowchartEditorConfigurator,
        sourceNodeId,
        reactFlowInstance,
        singletonTaskSchemasByName: addNodeParams.singletonTaskSchemasByName,
        canAddExistingTaskSchema: addNodeParams.taskDefinitionsForSidepanel.length > 0,
        rootTaskSchema: addNodeParams.rootTaskSchema,
        openSidepanelForNode,
        openSidepanelForNodeWithField,
        onNodeChangesOnly: (nodeChangeGenerator) => {
          applyFlowchartChanges((flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
            edgeChanges: [],
          }));
        },
        setSourceNodeAndNewNodeBeingAdded,
        didInitialFlowchartHaveChainedRouters: addNodeParams.didInitialFlowchartHaveChainedRouters,
      }),
    [addNodeParams, openSidepanelForNode, openSidepanelForNodeWithField, applyFlowchartChanges]
  );

  const interactiveFlowchartOptions: FlowchartOptions = useMemo(
    () => ({
      onNodeHeaderClick: openSidepanelForNode,
      onFieldClick: openSidepanelForNodeWithField,
      onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) =>
        applyFlowchartChanges(
          (flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
          }),
          saveImmediately,
          skipSettingDirtyState
        ),
      getAddNodeOptionsForRightHandle,
      validationErrors,
    }),
    [
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      getAddNodeOptionsForRightHandle,
      validationErrors,
      applyFlowchartChanges,
    ]
  );

  const [elkEdgeDataByEdgeId, setElkEdgeDataByEdgeId] = useState({});
  useEffect(() => {
    if (!staticFlowchart) {
      return;
    }

    // Use current positions from ref to preserve drag positions
    // Falls back to local state for nodes that don't exist yet (e.g., newly added)
    const positionsWithFallback = {
      ...nodePositions,
      ...currentNodePositionsRef.current,
    };

    const {nodes, edges} = buildInteractiveFlowchartFromStaticData({
      staticNodesData: staticFlowchart.nodesStaticData,
      staticEdgesData: staticFlowchart.edgesStaticData,
      options: interactiveFlowchartOptions,
      nodePositionsByNodeId: positionsWithFallback,
      selectedNodeIds: flowchartConfigMetadata.interactionState.selectedNodeIds,
      selectedEdgeIds: flowchartConfigMetadata.interactionState.selectedEdgeIds,
      elkEdgeDataByEdgeId,
      validationErrors,
    });

    nodes.forEach((node) => {
      currentNodePositionsRef.current[node.id] = node.position;
    });

    setInteractiveNodes(nodes);
    setInteractiveEdges(edges);
    // Intentionally exclude nodePositions to avoid rebuild on every drag.
    // When staticFlowchart changes (e.g. node added), this effect runs and reads nodePositions for new nodes.
    // But we don't add it to deps because position-only changes shouldn't trigger rebuilds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    staticFlowchart,
    flowchartConfigMetadata.interactionState.selectedNodeIds,
    flowchartConfigMetadata.interactionState.selectedEdgeIds,
    interactiveFlowchartOptions,
    elkEdgeDataByEdgeId,
    validationErrors,
  ]);

  // Handle adding nodes from right handle
  useEffect(() => {
    if (_sourceNodeAndNewNodeBeingAdded != null) {
      const [sourceNodeId, newNode] = _sourceNodeAndNewNodeBeingAdded;
      const isPlaceholder = isInteractivePlaceholderNode(newNode);
      applyFlowchartChanges(
        (flowchartDraftState) => ({
          nodeChanges: [
            {
              type: 'add',
              item: {
                ...newNode,
                data: {
                  ...newNode.data,
                  getAddNodeOptionsForRightHandle,
                },
              },
            },
          ],
          edgeChanges: [
            {
              type: 'add',
              item: buildControlFlowEdge({
                sourceNodeId,
                targetNodeId: newNode.id,
              }),
            },
          ],
        }),
        false,
        isPlaceholder
      );

      if (isPlaceholder) {
        setPlaceholderNodeBeingAdded(newNode.id);
      }

      setSourceNodeAndNewNodeBeingAdded(null);
    }
  }, [
    _sourceNodeAndNewNodeBeingAdded,
    setSourceNodeAndNewNodeBeingAdded,
    applyFlowchartChanges,
    getAddNodeOptionsForRightHandle,
  ]);

  // Handle opening sidepanel for placeholder nodes
  useEffect(() => {
    if (placeholderNodeIdBeingAdded != null) {
      // Check if the node actually exists in interactiveNodes
      const nodeExists = interactiveNodes.some((node) => node.id === placeholderNodeIdBeingAdded);

      if (!nodeExists) {
        // Node hasn't been added to interactiveNodes yet, wait for next render
        return;
      }

      // Select the placeholder node
      applyFlowchartChanges((flowchartDraftState) => ({
        nodeChanges: [
          {
            type: 'select',
            id: placeholderNodeIdBeingAdded,
            selected: true,
          },
        ],
      }));
      // Open the sidepanel
      openSidepanelForNode(placeholderNodeIdBeingAdded);
      setPlaceholderNodeBeingAdded(null);
    }
  }, [
    placeholderNodeIdBeingAdded,
    openSidepanelForNode,
    setPlaceholderNodeBeingAdded,
    applyFlowchartChanges,
    interactiveNodes,
  ]);

  // Should only be used for reactflow-specific node changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowNodeChangesOnly = useCallback(
    (nodeChanges: Array<FlowchartNodeChange>) => {
      const positionChanges = nodeChanges.filter((c) => c.type === FlowchartNodeChangeType.POSITION);
      // We ignore dimension changes (height and width of a node). Autolayout is the only code that relies on this as of this writing.
      // However, dimensions don't seem to propagate from reactflow when the flowchart is locked. Instead,
      // we expect autolayout code to grab the dimensions from the node internals on its own.
      const otherChanges = nodeChanges.filter(
        (c) => c.type !== FlowchartNodeChangeType.POSITION && c.type !== FlowchartNodeChangeType.DIMENSIONS
      );

      if (positionChanges.length > 0) {
        // Handle position changes locally using ReactFlow's applyNodeChanges
        // This avoids triggering Redux updates and full flowchart rebuilds during dragging
        setInteractiveNodes((nodes) => {
          const updatedNodes = applyNodeChanges(positionChanges, nodes);
          updatedNodes.forEach((node: Node) => {
            currentNodePositionsRef.current[node.id] = node.position;
          });
          return updatedNodes;
        });

        // Also update local position state
        positionChanges.forEach((change) => {
          if (change.position) {
            setNodePositions((prevPositions) => ({
              ...prevPositions,
              [change.id]: change.position as {x: number; y: number},
            }));
          }
        });
      }

      if (otherChanges.length > 0) {
        applyFlowchartChanges((flowchartConfig) => ({nodeChanges: otherChanges, edgeChanges: []}));
      }
    },
    [applyFlowchartChanges]
  );

  // Should only be used for reactflow-specific edge changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowEdgeChangesOnly = useCallback(
    (edgeChanges: Array<FlowchartEdgeChange>) => {
      applyFlowchartChanges((flowchartConfig) => ({nodeChanges: [], edgeChanges}));
    },
    [applyFlowchartChanges]
  );

  const handleRightSidepanelClose = useCallback(() => {
    const nodeIdToDeselect = selectedNodeIdForRightSidepanel;
    setIsRightSidepanelOpen(false);
    setSelectedNodeIdForRightSidepanel(null);
    setSelectedFieldId(null);
    if (nodeIdToDeselect) {
      applyFlowchartChanges((flowchartConfig) => ({
        nodeChanges: [
          {
            type: FlowchartNodeChangeType.SELECT,
            id: nodeIdToDeselect,
            selected: false,
          },
        ],
      }));
    }
  }, [applyFlowchartChanges, selectedNodeIdForRightSidepanel]);

  // Close sidepanel if the selected node is deleted
  useEffect(() => {
    if (selectedNodeIdForRightSidepanel != null && isRightSidepanelOpen) {
      const nodeStillExists = staticFlowchart.nodesStaticData.some(
        (node) => node.id === selectedNodeIdForRightSidepanel
      );
      if (!nodeStillExists) {
        handleRightSidepanelClose();
      }
    }
  }, [
    selectedNodeIdForRightSidepanel,
    isRightSidepanelOpen,
    staticFlowchart.nodesStaticData,
    handleRightSidepanelClose,
  ]);

  const onConnect = useCallback(
    (connection: Connection): void => {
      if (
        connection.source != null &&
        connection.target != null &&
        connection.sourceHandle != null &&
        connection.targetHandle != null
      ) {
        const sourceHandleType = getEdgeTypeFromHandleId(connection.sourceHandle);
        const targetHandleType = getEdgeTypeFromHandleId(connection.targetHandle);

        const edgeType =
          sourceHandleType === targetHandleType ? sourceHandleType : ProcessFlowchartEdgeType.CONTROL_FLOW;

        applyFlowchartChanges((flowchartConfig) => ({
          edgeChanges: [
            {
              type: FlowchartEdgeChangeType.ADD,
              item: {
                id: uuidv4(),
                source: connection.source!,
                target: connection.target!,
                sourceHandle: connection.sourceHandle,
                targetHandle: connection.targetHandle,
                type: edgeType,
              },
            },
          ],
        }));
      }
    },
    [applyFlowchartChanges]
  );

  const setNodesAndEdgesViaChangesForLayout = useCallback(
    (newNodes: Array<Node>, newEdges: Array<Edge>): void => {
      // Use unstable_batchedUpdates to ensure both state updates happen in a single render cycle.
      // NOTE: This is not necessary in React 18
      // Without this, updating nodes and edges separately can cause an interstitial rebuild
      // to fire between the two updates
      // Note: unstable_batchedUpdates is actually quite stable. https://stackoverflow.com/a/48610973
      unstable_batchedUpdates(() => {
        // Update node positions
        const nodeChanges: Array<FlowchartNodeChange> = newNodes.map((node) => ({
          type: FlowchartNodeChangeType.POSITION,
          id: node.id,
          position: node.position,
        }));
        onReactFlowNodeChangesOnly(nodeChanges);

        // Update edge layout data
        const newElkEdgeDataByEdgeId = newEdges.reduce(
          (acc, e) => {
            acc[e.id] = e.data.elkEdgeData;
            return acc;
          },
          {} as Record<string, ElkEdgeData>
        );
        setElkEdgeDataByEdgeId(newElkEdgeDataByEdgeId);
      });
    },
    [setElkEdgeDataByEdgeId, onReactFlowNodeChangesOnly]
  );

  // Reverts node + field selection back to a specific node and field
  const revertNodeSelection = useCallback(
    (nodeId: string, fieldId: string | null) => {
      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes
        const currentlySelectedNodeIds =
          flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds;
        currentlySelectedNodeIds.forEach((selectedId: string) => {
          if (selectedId !== nodeId) {
            nodeChanges.push({
              type: FlowchartNodeChangeType.SELECT,
              id: selectedId,
              selected: false,
            });
          }
        });

        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });

        return {nodeChanges};
      }).then(() => {
        setSelectedNodeIdForRightSidepanel(nodeId);
        setSelectedFieldId(fieldId);
        setInteractiveNodes((currentNodes: Array<FlowchartNode>) =>
          currentNodes.map((node: FlowchartNode) => ({
            ...node,
            selected: node.id === nodeId,
          }))
        );
      });
    },
    [applyFlowchartChanges, setSelectedNodeIdForRightSidepanel, setSelectedFieldId, setInteractiveNodes]
  );

  const handleDeleteKeyPress = useCallback(
    (event: KeyboardEvent): void => {
      if (locked || !keyboard.isADeleteKey(event)) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const selectedEdges = interactiveEdges.filter((edge) => edge.selected);
      const selectedNodes = interactiveNodes.filter((node) => node.selected);

      if (selectedEdges.length === 0 && selectedNodes.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // If only edges are selected, delete them immediately without confirmation
      if (selectedEdges.length > 0 && selectedNodes.length === 0) {
        const edgeChanges = selectedEdges.map((edge) => ({
          type: 'remove' as const,
          id: edge.id,
        }));
        onReactFlowEdgeChangesOnly(edgeChanges);
        return;
      }

      // If nodes are selected (with or without edges), show confirmation modal
      if (selectedNodes.length > 0) {
        const nodeNames = selectedNodes.map((node) => node.data.nodeName);

        const edgeChanges =
          selectedEdges.length > 0
            ? selectedEdges.map((edge) => ({
                type: 'remove' as const,
                id: edge.id,
              }))
            : [];

        const nodeChanges = selectedNodes.map((node) => ({
          type: 'remove' as const,
          id: node.id,
        }));

        const nodeIds = new Set(selectedNodes.map((node) => node.id));

        runModal(FlowchartRemoveNodeModal, {
          nodeNames,
          onRequestRemove: () => {
            nodesBeingDeletedRef.current = nodeIds;

            const isDeletingSelectedNode =
              selectedNodeIdForRightSidepanel != null && nodeIds.has(selectedNodeIdForRightSidepanel);

            if (isDeletingSelectedNode) {
              handleRightSidepanelClose();
            }

            if (edgeChanges.length > 0) {
              onReactFlowEdgeChangesOnly(edgeChanges);
            }

            onReactFlowNodeChangesOnly(nodeChanges);

            setTimeout(() => {
              nodesBeingDeletedRef.current = null;
            });
          },
        });
      }
    },
    [
      locked,
      interactiveNodes,
      interactiveEdges,
      onReactFlowEdgeChangesOnly,
      onReactFlowNodeChangesOnly,
      selectedNodeIdForRightSidepanel,
      handleRightSidepanelClose,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleDeleteKeyPress);
    return () => {
      document.removeEventListener('keydown', handleDeleteKeyPress);
    };
  }, [handleDeleteKeyPress]);

  return {
    // State
    interactiveNodes,
    interactiveEdges,
    setInteractiveNodes,
    // Core functions
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    setNodesAndEdgesViaChangesForLayout,
    // Sidepanel state
    isLeftSidepanelOpen,
    setIsLeftSidepanelOpen,
    isRightSidepanelOpen,
    selectedFieldId,
    selectedNodeIdForRightSidepanel,
    setSelectedFieldId,
    setSelectedNodeIdForRightSidepanel,
    // Sidepanel functions
    handleRightSidepanelClose,
    revertNodeSelection,
    // Node addition state
    interactiveFlowchartOptions,
    setPlaceholderNodeBeingAdded,
    // Node deletion state
    nodesBeingDeletedRef,
    // Node dimensions
    getMeasuredDimensionsFromStore,
  };
};

export default useFlowchartState;
